import React, { useState } from 'react'
import Modal from 'react-modal'
import { date, notify } from '@/_helpers'
import { API, Card, Table, Submit, Select, Input, Date, Help, useSubmit } from '@enkla-gui'
import AddonConnectModal from './connect.js'
import Tippy from '@tippyjs/react'

export default function Addons({ lease, callback }) {
  const [connectModal, setConnectModal] = useState(false)
  const { register, reset, control, sending, error, modal, setModal, post, handleSubmit } = useSubmit({
    endpoint: 'api/leaseaddon',
    success: () => {
      reset()
      callback()
    }
  })
  const submit = handleSubmit(payload => {
    if (!payload.start) payload.start = date.today()
    return post(payload)
  })
  const {
    register: updateRegister,
    reset: updateReset,
    control: updateControl,
    modal: updateModal,
    setModal: setUpdateModal,
    error: updateError,
    sending: updateSending,
    post: updatePost,
    handleSubmit: updateHandleSubmit
  } = useSubmit({
    endpoint: 'api/leaseaddon',
    method: 'patch',
    success: () => callback()
  })
  const updateSubmit = updateHandleSubmit(payload => {
    if (!payload.start) payload.start = date.today()
    return updatePost(payload)
  })

  const remove = async target => {
    if (!(await notify.confirm())) return
    API.delete(`api/leaseaddon/${target.id}`)
      .then(res => {
        notify.toast('Tillägget har tagits bort')
        callback()
      })
      .catch(([err, res]) => {
        notify.toast(`Borttagningen misslyckades: ${res.message}`, 'error')
      })
  }
  if (lease.addons === undefined) return false
  return (
    <>
      <Card
        title='Tillägg & Avdrag'
        icon='fas fa-fw fa-receipt'
        collapsed={lease.addons.length === 0}
        buttons={{
          header: [
            {
              icon: 'fas fa-plus-square', text: 'Skapa', callback: () => setModal(true),
              hidden: lease?.state?.status === 'inactive'
            },
            {
              icon: 'fas fa-link', text: 'Koppla', callback: () => setConnectModal(true),
              hidden: lease?.state?.status === 'inactive'
            },
          ]
        }}>
        <Table
          rows={lease.addons}
          empty={{ text: 'Det finns inte några tillägg eller avdrag', icon: 'fas fa-receipt' }}
          functions={{
            setAddon: addon =>
              addon.addon_id === null ? (
                <button
                  class='btn btn-light btn-sm'
                  onClick={() => {
                    updateReset(addon)
                    setUpdateModal(true)
                  }}>
                  <i class='fas fa-edit' />
                </button>
              ) : (
                <a class='btn btn-sm btn-light'>
                  <Tippy content={`Detta är ett globalt ${addon.type.toLowerCase()} och kan därför inte redigeras på en uthyrning`}>
                    <i class='fas fa-fw fa-globe' />
                  </Tippy>
                </a>
              ),
            type: addon => (addon.fee >= 0 ? <span class='badge bg-success w-100'>Tillägg</span> : <span class='badge bg-default w-100'>Avdrag</span>),
            remove: addon => remove(addon)
          }}
          keys={[
            '_this>function:setAddon:fa-edit',
            '_this>function:type',
            'name',
            'fee>currency',
            'tax_rate>%',
            'start>date:short',
            '_this>button:remove:fa-trash-alt'
          ]}>
          <th class='min' />
          <th class='min'>Typ</th>
          <th>Namn</th>
          <th>Belopp</th>
          <th>Momssats</th>
          <th class='min'>Startdatum</th>
          <th class='min' />
        </Table>
      </Card>
      <AddonConnectModal modal={connectModal} setModal={setConnectModal} id={lease.id} fetch={() => callback()} />
      <Modal isOpen={modal} onRequestClose={() => setModal(false)} className='no-padding'>
        <Card
          title='Skapa Tillägg & Avdrag'
          collapsed={null}
          icon='fas fa-fw fa-receipt'
          buttons={{
            header: [{ callback: () => setModal(false), icon: 'fas fa-times', text: 'Avbryt', bg: 'light' }],
            footer: [<Submit onSubmit={submit} text='Skapa' sending={sending} />]
          }}>
          <input type='hidden' {...register('lease_id')} value={lease.id} />
          <Help title='Information' type='info'>
            <p>
              Här kan du skapa upp rader för tillägg och avdrag som gjorts. Detta kommer att användas vid uträkning av månadskostnad. Intervallet är detsamma
              som är angivet på uthyrningen
            </p>
            <p>
              Ett <b>tillägg</b> skapas genom att fylla i ett positivt värde
            </p>
            <p>
              Ett <b>avdrag</b> skapas genom att fylla i ett negativt värde
            </p>
          </Help>
          <div class='row mb-3'>
            <Input controls={{ register }} name='name' options={{ required: true }} label='Namn' error={error} />
          </div>
          <div class='row mb-3'>
            <Input controls={{ register }} name='fee' options={{ required: true }} type='number' label='Belopp' error={error} />
          </div>
          <div class='row mb-3'>
            <Select
              controls={{ register }}
              name='tax_rate'
              options={{ required: true }}
              label='Momssats(%)'
              error={error}
              values={[
                { key: '0%', value: 0 },
                { key: '6%', value: 6 },
                { key: '12%', value: 12 },
                { key: '25%', value: 25 }
              ]}
            />
          </div>
          <div class='row mb-3'>
            <Date today label='Startdatum för debitering' controls={{ control }} name='start' error={error} placeholder='Välj ett datum, vid tomt värde anges dagens datum' />
          </div>
        </Card>
      </Modal>
      <Modal isOpen={updateModal} onRequestClose={() => setUpdateModal(false)} className='no-padding'>
        <Card
          title='Redigera Tillägg & Avdrag'
          collapsed={null}
          icon='fas fa-fw fa-receipt'
          buttons={{
            header: [{ callback: () => setModal(false), icon: 'fas fa-times', text: 'Avbryt', bg: 'light' }],
            footer: [<Submit onSubmit={updateSubmit} text='Uppdatera' sending={updateSending} />]
          }}>
          <input type='hidden' {...updateRegister('lease_id')} value={lease.id} />
          <Help title='Information' type='info'>
            <p>
              Här kan du skapa upp rader för tillägg och avdrag som gjorts. Detta kommer att användas vid uträkning av månadskostnad. Intervallet är detsamma
              som är angivet på uthyrningen
            </p>
            <p>
              Ett <b>tillägg</b> skapas genom att fylla i ett positivt värde
            </p>
            <p>
              Ett <b>avdrag</b> skapas genom att fylla i ett negativt värde
            </p>
          </Help>
          <div class='row mb-3'>
            <div class='row mb-3'>
              <Input controls={{ register: updateRegister }} name='name' options={{ required: true }} label='Namn' error={updateError} />
            </div>
            <div class='row mb-3'>
              <Input controls={{ register: updateRegister }} name='fee' options={{ required: true }} label='Belopp' type='number' error={updateError} />
            </div>
            <div class='row mb-3'>
              <Select
                controls={{ register: updateRegister }}
                name='tax_rate'
                options={{ required: true }}
                label='Momssats(%)'
                error={updateError}
                values={[
                  { key: '0%', value: 0 },
                  { key: '6%', value: 6 },
                  { key: '12%', value: 12 },
                  { key: '25%', value: 25 }
                ]}
              />
            </div>
            <div class='row'>
              <Date today label='Startdatum för debitering' controls={{ control: updateControl }} name='start' error={updateError} placeholder='Välj ett datum, vid tomt värde anges dagens datum' />
            </div>
          </div>
        </Card>
      </Modal>
    </>
  )
}
